import * as React from "react"
import { Link, GatsbyLinkProps } from "gatsby"
import { LinkGetProps } from "@reach/router"

// <TState> is copied from the Gatsby Link type. It represents a placeholder for a specific type
// that can be determined when using or implementing the interface
interface LinkWrapperProps<TState> extends GatsbyLinkProps<TState> {
  /** A class to apply when this link is active */
  activeClassName?: string
  /** The content to render inside the link */
  children: React.ReactNode
  /** A class to apply to the link */
  className?: string
  /**
   * Calls up to you to get props for the underlying anchor element. Useful for styling the anchor as active.
   * See https://reach.tech/router/api/Link
   */
  getProps?: ((props: LinkGetProps) => {}) | undefined
  /** Where to open the linked document when clicked. Typically undefined or `_blank` to open the linked document in a new tab/window. */
  target?: string
  /** Whether to include the active className on the link if current url starts with the `to` value for this link */
  partiallyActive?: boolean
  /** Required for the forwardRef to work (which is needed to use this component with libraries that require refs) */
  ref?: React.Ref<HTMLAnchorElement> // TODO: The type does not appear to be the same for GatsbyLink and a plain `a` tag, so this can be revisited
  /** The URL you want to link to */
  to: string
  /** May be passed from other components, intentially omitted before returning the render function since they are valid props */
  buttonTheme?: string
  /** May be passed from other components, intentially omitted before returning the render function since they are valid props */
  buttonType?: string
}

/**
 * Helper component for handling links that uses `gatsby-link` for internal links and `<a/>` for others (e.g. special paths, files, and external links)
 */
const LinkWrapper = React.forwardRef<HTMLAnchorElement, LinkWrapperProps<any>>(
  (props: LinkWrapperProps<any>, ref) => {
    const {
      to,
      children,
      buttonTheme, // This is destructured to prevent it from being passed to the <a> tag
      buttonType, // This is destructured to prevent it from being passed to the <a> tag
      target,
      ...other
    } = props
    const internal = /^\/(?!\/)/.test(to)
    const file = /\.[0-9a-z]+$/i.test(to)
    // Exceptions are pages that should not use the Gatsby link element
    // For example, `/support/phone-numbers so Netlify can handle country-based redirects
    const exception = /(\/support\/phone-numbers$|\/media$|\/corporate$)/i.test(
      to
    )

    // Only render Gatsby Link for internal links. Do not use it when linking to a file, an excepted URL, or when a target is specified (which would open the link in a new tab)
    if (internal && !file && !exception && !target) {
      return (
        <Link ref={ref} to={to} {...other}>
          {children}
        </Link>
      )
    } else {
      const { partiallyActive, activeClassName, getProps, ...linkProps } = other
      // TODO:
      // - Add support for `activeClassName` and `getProps` for external links. I think this may be possible with `useLocation` and `useMatch` from `@reach/router`
      return (
        <a
          href={to}
          {...linkProps}
          target={target ? target : file ? "_blank" : undefined}
          ref={ref}
        >
          {children}
        </a>
      )
    }
  }
)

export default LinkWrapper
